import type { StepEffect, TutorialStep } from './types';

function applyEffect(effect: StepEffect): void {
  const el = document.querySelector<HTMLElement>(effect.selector);
  if (!el) return;
  if (effect.action === 'setStyle' && effect.value !== undefined) {
    el.style.setProperty(effect.property, effect.value);
  } else if (effect.action === 'removeStyle') {
    el.style.removeProperty(effect.property);
  }
}

function hydrateStep(raw: Record<string, unknown>): TutorialStep {
  const step: TutorialStep = {
    id: raw.id as string,
    targetSelector: raw.targetSelector as string,
    title: raw.title as string,
    body: raw.body as string,
    placement: raw.placement as TutorialStep['placement'],
    route: raw.route as string | undefined,
    spotlightPadding: raw.spotlightPadding as number | undefined,
    unblurSelectors: raw.unblurSelectors as string[] | undefined,
    advanceOn: raw.advanceOn as TutorialStep['advanceOn'],
    transition: raw.transition as TutorialStep['transition'],
    overlay: raw.overlay as TutorialStep['overlay'],
    optional: raw.optional as boolean | undefined,
    useWhen: raw.useWhen as string | undefined,
    tags: raw.tags as string[] | undefined,
  };

  const effects = raw.effects as
    | { onEnter?: StepEffect[]; onExit?: StepEffect[] }
    | undefined;
  if (effects) {
    step.effects = effects;
    if (effects.onEnter?.length) {
      step.onEnter = () => effects.onEnter!.forEach(applyEffect);
    }
    if (effects.onExit?.length) {
      step.onExit = () => effects.onExit!.forEach(applyEffect);
    }
  }

  return step;
}

interface TutorialMeta {
  name: string;
  description: string;
  stepIds: string[];
}

interface SectionEntry {
  id: string;
  file: string;
  description: string;
  routes: string[];
}

interface ManifestJson {
  sections: SectionEntry[];
  tutorials: Record<string, TutorialMeta>;
}

interface SectionJson {
  section: string;
  steps: Record<string, unknown>[];
}

// Steps indexed by composite "section:stepId" key
let _registry: Record<string, TutorialStep> = {};
// Also index by bare stepId for backward compatibility
let _bareIndex: Record<string, TutorialStep> = {};
let _tutorials: Record<string, TutorialMeta> = {};
let _sections: SectionEntry[] = [];
let _loadPromise: Promise<void> | null = null;

function loadSection(entry: SectionEntry): Promise<void> {
  return fetch(`/tutorial/${entry.file}`)
    .then(r => r.json() as Promise<SectionJson>)
    .then(data => {
      for (const raw of data.steps) {
        const step = hydrateStep(raw);
        const compositeId = `${entry.id}:${step.id}`;
        _registry[compositeId] = step;
        _bareIndex[step.id] = step;
      }
    })
    .catch(() => {
      // Section failed to load — skip silently
    });
}

function doLoad(): Promise<void> {
  return fetch('/tutorial/manifest.json')
    .then(r => r.json() as Promise<ManifestJson>)
    .then(manifest => {
      _tutorials = manifest.tutorials ?? {};
      _sections = manifest.sections ?? [];
      return Promise.all(_sections.map(loadSection));
    })
    .then(() => {})
    .catch(() => {
      // Manifest failed — registry stays empty
    });
}

export function loadStepRegistry(): Promise<void> {
  if (!_loadPromise) _loadPromise = doLoad();
  return _loadPromise;
}

export function getStepRegistry(): Record<string, TutorialStep> {
  return _registry;
}

export function getStep(id: string): TutorialStep | undefined {
  return _registry[id] ?? _bareIndex[id];
}

export function getSteps(ids: string[]): TutorialStep[] {
  const steps: TutorialStep[] = [];
  for (const id of ids) {
    const step = getStep(id);
    if (step) steps.push(step);
  }
  return steps;
}

export function getTutorialSteps(tutorialId: string): TutorialStep[] {
  const tutorial = _tutorials[tutorialId];
  if (!tutorial) return [];
  return getSteps(tutorial.stepIds);
}

export function getWelcomeTutorialSteps(): TutorialStep[] {
  return getTutorialSteps('welcome');
}

export function getTutorials(): Array<{
  id: string;
  name: string;
  description: string;
  stepCount: number;
}> {
  return Object.entries(_tutorials).map(([id, meta]) => ({
    id,
    name: meta.name,
    description: meta.description,
    stepCount: meta.stepIds.length,
  }));
}

export function getAllStepIds(): string[] {
  return Object.keys(_registry);
}

export function getSections(): SectionEntry[] {
  return _sections;
}
